import React from "react";
import jsPDF from "jspdf";

const KOTModal = ({ isOpen, onClose, order }) => {

  if (!isOpen || !order) return null;

  // Extract items safely
  const previewItems = (order.items?.split(",") || []).map(item =>
    item.replace(/\(₹.*?\)/, "").trim()
  );

  const instructions = order.cookingInstructions
    ? [order.cookingInstructions]
    : [];

  const generateKOT = () => {

    const itemCount = previewItems.length;
    const dynamicHeight = 140 + itemCount * 8;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [80, dynamicHeight],
    });

    const left = 5;
    const right = 75;
    const center = 40;
    let y = 8;

    // ===== HEADER =====
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("KITCHEN ORDER TICKET", center, y, { align: "center" });
    y += 6;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);

    const timeNow = new Date().toLocaleString("en-IN");

    if (order.tableNumber) {
      pdf.text(`Table: ${order.tableNumber}`, left, y);
      y += 5;
    }

    if (order.roomNumber) {
      pdf.text(`Room: ${order.roomNumber}`, left, y);
      y += 5;
    }

    pdf.text(`Time: ${timeNow}`, left, y);
    y += 5;

    pdf.line(left, y, right, y);
    y += 6;

    // ===== ITEMS =====
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);

    previewItems.forEach(item => {

      let safeItem = item.replace(/[^\x00-\x7F]/g, "").trim();
      const split = pdf.splitTextToSize(safeItem, 65);

      split.forEach(line => {
        pdf.text(line, left, y);
        y += 6;
      });

      y += 2;
    });

    pdf.line(left, y, right, y);
    y += 6;

    // ===== INSTRUCTIONS =====
    if (instructions.length > 0) {

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text("INSTRUCTIONS:", left, y);
      y += 5;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);

      instructions.forEach(note => {

        let safeNote = note.replace(/[^\x00-\x7F]/g, "").trim();
        const splitNote = pdf.splitTextToSize(`- ${safeNote}`, 65);

        splitNote.forEach(line => {
          pdf.text(line, left, y);
          y += 5;
        });

        y += 2;
      });

      pdf.line(left, y, right, y);
      y += 5;
    }

    // ===== PREFERRED TIME =====
    if (order.preferredTime) {
      pdf.setFont("helvetica", "bold");
      pdf.text(`Preferred At: ${order.preferredTime}`, left, y);
      y += 6;
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text("PREPARE FAST", center, y, { align: "center" });

    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6 flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Kitchen Order Ticket
            </h2>
            <p className="text-xs text-gray-500">
              Review before printing
            </p>
          </div>

          <span className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-full font-semibold">
            KOT
          </span>
        </div>

        {/* Order Info */}
        <div className="bg-gray-50 border rounded-xl p-4 text-sm mb-4">
          <div className="grid grid-cols-2 gap-3">

            <div>
              <p className="text-gray-500 text-xs">Order Type</p>
              <p className="font-medium">{order.orderType}</p>
            </div>

            {order.tableNumber && (
              <div>
                <p className="text-gray-500 text-xs">Table</p>
                <p className="font-medium">#{order.tableNumber}</p>
              </div>
            )}

            {order.roomNumber && (
              <div>
                <p className="text-gray-500 text-xs">Room</p>
                <p className="font-medium">#{order.roomNumber}</p>
              </div>
            )}

            <div>
              <p className="text-gray-500 text-xs">Customer</p>
              <p className="font-medium">{order.customerName}</p>
            </div>

          </div>
        </div>

        {/* Items Preview */}
        <div className="flex-1 overflow-y-auto border rounded-xl p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Items to Prepare
          </h3>

          <div className="space-y-2 text-sm">
            {previewItems.map((item, index) => (
              <div
                key={index}
                className="px-3 py-2 bg-white border rounded-lg shadow-sm"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        {instructions.length > 0 && (
          <div className="border border-red-200 bg-red-50 rounded-xl p-4 mb-4">
            <h3 className="text-sm font-semibold text-red-700 mb-2">
              Cooking Instructions
            </h3>

            <div className="text-sm text-red-800 space-y-1">
              {instructions.map((note, index) => (
                <div key={index}>- {note}</div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition"
          >
            Cancel
          </button>

          <button
            onClick={generateKOT}
            className="px-5 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-md transition"
          >
            Print KOT
          </button>
        </div>

      </div>
    </div>
  );
};

export default KOTModal;